import React, { useState, useEffect } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Badge } from '../../ui/Badge';
import { colors, agentThemes, spacing, borderRadius, shadows } from '../../../constants/theme';

interface Template {
  id: string;
  name: string;
  category: TemplateCategory;
  type: 'poster' | 'banner' | 'flyer' | 'pop' | 'digital' | 'sns';
  thumbnail: string;
  description: string;
  tags: string[];
  dimensions: {
    width: number;
    height: number;
    unit: 'px' | 'mm' | 'cm';
  };
  colors: string[];
  usage: number;
  rating: number;
  premium: boolean;
  customizable: CustomizableElements;
}

interface TemplateCategory {
  id: string;
  name: string;
  icon: string;
}

interface CustomizableElements {
  text: boolean;
  images: boolean;
  colors: boolean;
  layout: boolean;
}

interface TemplateFilter {
  category: string;
  type: string;
  search: string;
  tags: string[];
}

interface TemplateGalleryProps {
  onTemplateSelect?: (template: Template) => void;
  onTemplatePreview?: (template: Template) => void;
  selectedCategory?: string;
}

const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  onTemplateSelect,
  onTemplatePreview,
  selectedCategory = 'all'
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'popular' | 'recent' | 'rating'>('popular');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);

  // カテゴリー定義
  const categories: TemplateCategory[] = [
    { id: 'all', name: 'すべて', icon: '🎨' },
    { id: 'seasonal', name: '季節', icon: '🌸' },
    { id: 'sale', name: 'セール', icon: '🏷️' },
    { id: 'event', name: 'イベント', icon: '🎉' },
    { id: 'product', name: '商品訴求', icon: '📦' },
    { id: 'brand', name: 'ブランド', icon: '⭐' }
  ];

  // サンプルテンプレートデータ生成
  const generateTemplates = (): Template[] => {
    return [
      {
        id: 'tmpl001',
        name: '春の新生活応援セール',
        category: categories[1],
        type: 'poster',
        thumbnail: '🌸',
        description: '春らしい明るいデザインの販促ポスター',
        tags: ['春', 'セール', '新生活', 'パステル'],
        dimensions: { width: 420, height: 594, unit: 'mm' },
        colors: ['#FFB3D9', '#B3E5FF', '#FFE5B3', '#B3FFB3'],
        usage: 1250,
        rating: 4.8,
        premium: false,
        customizable: {
          text: true,
          images: true,
          colors: true,
          layout: false
        }
      },
      {
        id: 'tmpl002',
        name: 'タイムセール告知バナー',
        category: categories[2],
        type: 'banner',
        thumbnail: '⏰',
        description: '緊急感を演出するタイムセールバナー',
        tags: ['セール', 'タイムセール', '限定', '赤'],
        dimensions: { width: 1920, height: 600, unit: 'px' },
        colors: ['#E85C56', '#FFD700', '#FFFFFF', '#2D3138'],
        usage: 2340,
        rating: 4.6,
        premium: false,
        customizable: {
          text: true,
          images: false,
          colors: true,
          layout: false
        }
      },
      {
        id: 'tmpl003',
        name: '夏祭りイベント告知',
        category: categories[3],
        type: 'flyer',
        thumbnail: '🎆',
        description: '夏祭りの楽しい雰囲気を表現したフライヤー',
        tags: ['夏', 'イベント', '祭り', 'カラフル'],
        dimensions: { width: 210, height: 297, unit: 'mm' },
        colors: ['#FF6B6B', '#4ECDC4', '#FFE66D', '#1E90FF'],
        usage: 890,
        rating: 4.9,
        premium: true,
        customizable: {
          text: true,
          images: true,
          colors: true,
          layout: true
        }
      },
      {
        id: 'tmpl004',
        name: '新商品紹介POP',
        category: categories[4],
        type: 'pop',
        thumbnail: '🆕',
        description: '商品の特徴を分かりやすく伝えるPOP',
        tags: ['新商品', 'POP', 'シンプル', '商品紹介'],
        dimensions: { width: 150, height: 100, unit: 'mm' },
        colors: ['#4D8FEA', '#5CC8A1', '#FFFFFF', '#2D3138'],
        usage: 1560,
        rating: 4.7,
        premium: false,
        customizable: {
          text: true,
          images: true,
          colors: false,
          layout: false
        }
      },
      {
        id: 'tmpl005',
        name: 'SNS投稿用正方形',
        category: categories[5],
        type: 'sns',
        thumbnail: '📱',
        description: 'Instagram等のSNS投稿に最適な正方形デザイン',
        tags: ['SNS', 'Instagram', '正方形', 'モダン'],
        dimensions: { width: 1080, height: 1080, unit: 'px' },
        colors: ['#667EEA', '#764BA2', '#FFFFFF', '#2D3138'],
        usage: 3200,
        rating: 4.9,
        premium: false,
        customizable: {
          text: true,
          images: true,
          colors: true,
          layout: false
        }
      },
      {
        id: 'tmpl006',
        name: 'クリスマスセール',
        category: categories[1],
        type: 'poster',
        thumbnail: '🎄',
        description: 'クリスマスの特別感を演出するポスター',
        tags: ['クリスマス', 'セール', '冬', 'ゴールド'],
        dimensions: { width: 420, height: 594, unit: 'mm' },
        colors: ['#C41E3A', '#165B33', '#FFD700', '#FFFFFF'],
        usage: 980,
        rating: 4.8,
        premium: true,
        customizable: {
          text: true,
          images: true,
          colors: false,
          layout: true
        }
      },
      {
        id: 'tmpl007',
        name: 'ポイント2倍デー',
        category: categories[2],
        type: 'digital',
        thumbnail: '💳',
        description: 'ポイントキャンペーン用デジタルサイネージ',
        tags: ['ポイント', 'キャンペーン', 'デジタル'],
        dimensions: { width: 1920, height: 1080, unit: 'px' },
        colors: ['#F5A623', '#4D8FEA', '#FFFFFF', '#2D3138'],
        usage: 1450,
        rating: 4.5,
        premium: false,
        customizable: {
          text: true,
          images: false,
          colors: true,
          layout: false
        }
      },
      {
        id: 'tmpl008',
        name: 'オーガニック商品特集',
        category: categories[4],
        type: 'banner',
        thumbnail: '🌿',
        description: '自然派・オーガニック商品の訴求バナー',
        tags: ['オーガニック', '自然', 'グリーン', 'ヘルシー'],
        dimensions: { width: 728, height: 90, unit: 'px' },
        colors: ['#5CC8A1', '#8BC34A', '#FFF8DC', '#4B5F3A'],
        usage: 670,
        rating: 4.6,
        premium: false,
        customizable: {
          text: true,
          images: true,
          colors: false,
          layout: false
        }
      }
    ];
  };

  useEffect(() => {
    setTimeout(() => {
      setTemplates(generateTemplates());
      setLoading(false);
    }, 600);
  }, []);

  // ソート処理のみ
  const sortedTemplates = [...templates].sort((a, b) => {
    if (sortBy === 'popular') return b.usage - a.usage;
    if (sortBy === 'rating') return b.rating - a.rating;
    return 0;
  });

  const getTypeIcon = (type: Template['type']) => {
    const icons = {
      poster: '📄',
      banner: '🖼️',
      flyer: '📋',
      pop: '🏷️',
      digital: '💻',
      sns: '📱'
    };
    return icons[type];
  };

  const renderTemplateCard = (template: Template) => (
    <Card
      key={template.id}
      onClick={() => {
        setSelectedTemplate(template);
        onTemplateSelect?.(template);
      }}
      style={{
        cursor: 'pointer',
        border: selectedTemplate?.id === template.id 
          ? `2px solid ${agentThemes.creative.primary}`
          : `1px solid ${colors.support.gray}`,
        transition: 'all 200ms ease',
        overflow: 'hidden'
      }}
    >
      {/* サムネイル */}
      <div style={{
        height: viewMode === 'grid' ? '180px' : '100px',
        backgroundColor: colors.background.secondary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        background: `linear-gradient(135deg, ${template.colors[0]} 0%, ${template.colors[1]} 100%)`
      }}>
        <div style={{ fontSize: '3rem' }}>{template.thumbnail}</div>
        {template.premium && (
          <Badge
            style={{
              position: 'absolute',
              top: spacing.sm,
              right: spacing.sm,
              backgroundColor: '#FFD700',
              color: '#000'
            }}
            size="sm"
          >
            Premium
          </Badge>
        )}
        {viewMode === 'grid' && (
          <div style={{
            position: 'absolute',
            top: spacing.sm,
            left: spacing.sm
          }}>
            {getTypeIcon(template.type)}
          </div>
        )}
      </div>

      {/* 情報部分 */}
      <div style={{ padding: spacing.md }}>
        <div style={{ marginBottom: spacing.sm }}>
          <h3 style={{
            fontSize: viewMode === 'grid' ? '1rem' : '0.875rem',
            fontWeight: 600,
            color: colors.text.dark,
            marginBottom: spacing.xs
          }}>
            {template.name}
          </h3>
          <p style={{
            fontSize: '0.75rem',
            color: colors.text.light,
            marginBottom: spacing.sm,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: viewMode === 'grid' ? 'normal' : 'nowrap'
          }}>
            {template.description}
          </p>
        </div>

        {/* タグ */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          marginBottom: spacing.sm
        }}>
          {template.tags.slice(0, viewMode === 'grid' ? 3 : 2).map(tag => (
            <Badge key={tag} size="sm" variant="secondary">
              {tag}
            </Badge>
          ))}
          {template.tags.length > (viewMode === 'grid' ? 3 : 2) && (
            <Badge size="sm" variant="secondary">
              +{template.tags.length - (viewMode === 'grid' ? 3 : 2)}
            </Badge>
          )}
        </div>

        {/* メトリクス */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: spacing.sm,
          borderTop: `1px solid ${colors.support.gray}`
        }}>
          <div style={{ display: 'flex', gap: spacing.md }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '0.75rem', color: colors.text.light }}>⭐</span>
              <span style={{ fontSize: '0.875rem', fontWeight: 500, color: colors.text.dark }}>
                {template.rating}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '0.75rem', color: colors.text.light }}>👥</span>
              <span style={{ fontSize: '0.875rem', color: colors.text.secondary }}>
                {template.usage.toLocaleString()}
              </span>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onTemplatePreview?.(template);
            }}
          >
            プレビュー
          </Button>
        </div>
      </div>
    </Card>
  );

  return (
    <div style={{ padding: spacing.lg }}>
      {/* ヘッダー */}
      <div style={{ marginBottom: spacing.lg }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: spacing.md
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: colors.text.dark,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm
          }}>
            <span>{agentThemes.creative.icon}</span>
            テンプレートギャラリー
          </h2>
          <div style={{ display: 'flex', gap: spacing.sm }}>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              style={{ width: '120px' }}
            >
              <option value="popular">人気順</option>
              <option value="recent">新着順</option>
              <option value="rating">評価順</option>
            </Select>
            <div style={{ display: 'flex', border: `1px solid ${colors.support.gray}`, borderRadius: borderRadius.sm }}>
              <button
                onClick={() => setViewMode('grid')}
                style={{
                  padding: `${spacing.xs} ${spacing.sm}`,
                  backgroundColor: viewMode === 'grid' ? colors.background.secondary : colors.background.primary,
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: `${borderRadius.sm} 0 0 ${borderRadius.sm}`
                }}
              >
                <span style={{ fontSize: '1rem' }}>⊞</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  padding: `${spacing.xs} ${spacing.sm}`,
                  backgroundColor: viewMode === 'list' ? colors.background.secondary : colors.background.primary,
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: `0 ${borderRadius.sm} ${borderRadius.sm} 0`
                }}
              >
                <span style={{ fontSize: '1rem' }}>☰</span>
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* テンプレート一覧 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ color: colors.text.light }}>テンプレートを読み込み中...</div>
        </div>
      ) : sortedTemplates.length === 0 ? (
        <Card>
          <div style={{ padding: spacing.xl, textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: spacing.md }}>🔍</div>
            <div style={{ fontSize: '1.125rem', fontWeight: 600, color: colors.text.dark, marginBottom: spacing.sm }}>
              該当するテンプレートが見つかりません
            </div>
            <div style={{ fontSize: '0.875rem', color: colors.text.light }}>
              フィルター条件を変更してお試しください
            </div>
          </div>
        </Card>
      ) : (
        <div style={{
          display: viewMode === 'grid' ? 'grid' : 'flex',
          gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(280px, 1fr))' : '1fr',
          flexDirection: viewMode === 'list' ? 'column' : undefined,
          gap: spacing.md
        }}>
          {sortedTemplates.map(template => renderTemplateCard(template))}
        </div>
      )}

      {/* 選択したテンプレートの詳細パネル */}
      {selectedTemplate && (
        <div style={{
          position: 'fixed',
          bottom: spacing.lg,
          right: spacing.lg,
          width: '320px',
          backgroundColor: colors.background.primary,
          borderRadius: borderRadius.md,
          boxShadow: shadows.xl,
          zIndex: 1000
        }}>
          <Card>
            <div style={{ padding: spacing.md }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.md }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: colors.text.dark
                }}>
                  {selectedTemplate.name}
                </h3>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1.25rem',
                    color: colors.text.light
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: spacing.md }}>
                <div style={{ fontSize: '0.75rem', color: colors.text.light, marginBottom: spacing.xs }}>
                  サイズ
                </div>
                <div style={{ fontSize: '0.875rem', color: colors.text.dark }}>
                  {selectedTemplate.dimensions.width} × {selectedTemplate.dimensions.height} {selectedTemplate.dimensions.unit}
                </div>
              </div>

              <div style={{ marginBottom: spacing.md }}>
                <div style={{ fontSize: '0.75rem', color: colors.text.light, marginBottom: spacing.xs }}>
                  カスタマイズ可能要素
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs }}>
                  {selectedTemplate.customizable.text && <Badge size="sm">テキスト</Badge>}
                  {selectedTemplate.customizable.images && <Badge size="sm">画像</Badge>}
                  {selectedTemplate.customizable.colors && <Badge size="sm">カラー</Badge>}
                  {selectedTemplate.customizable.layout && <Badge size="sm">レイアウト</Badge>}
                </div>
              </div>

              <Button
                onClick={() => {
                  onTemplateSelect?.(selectedTemplate);
                  // チラシ作成アクションを実行
                  alert(`✅ テンプレート「${selectedTemplate.name}」を使用してチラシを作成しました！\n\n作成内容:\n- サイズ: ${selectedTemplate.dimensions.width} × ${selectedTemplate.dimensions.height} ${selectedTemplate.dimensions.unit}\n- タイプ: ${selectedTemplate.type}\n- カテゴリ: ${selectedTemplate.category.name}\n\n編集画面が開きます。`);
                }}
                style={{
                  width: '100%',
                  backgroundColor: agentThemes.creative.primary,
                  color: '#FFFFFF'
                }}
              >
                このテンプレートでチラシを作成
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TemplateGallery;